/** Contrato de dados compartilhado entre a API (Netlify Functions) e o front-end. */

export type Regime = "tradicional" | "desonerada";
export type Papel = "admin" | "usuario";
export type TipoComposicao = "mobilizacao" | "desmobilizacao" | "administracao_local";
export type TipoBdi = "servicos" | "materiais";
/** Como a parcela de material do código foi obtida (ver importers/import_decomposicao.py). */
export type StatusDecomposicao = "composicao" | "incompleta" | "sem_composicao";

export const TIPOS_COMPOSICAO: TipoComposicao[] = ["mobilizacao", "desmobilizacao", "administracao_local"];

export interface Usuario {
  id: number;
  nome: string;
  email: string;
  papel: Papel;
  pode_editar: boolean;
  criado_em: string;
  /** false = cadastro aguardando aprovação do administrador. */
  aprovado?: boolean;
  /** Só na lista de Usuários (admin): pediu nova senha pela tela de login. */
  pediu_nova_senha?: boolean;
}

export interface Familia {
  id: number;
  codigo: string;
  nome: string;
}

export interface Sindicato {
  id: number;
  sigla: string;
  nome: string | null;
  base_territorial: string | null;
  confirmado: boolean;
  ls_h: number | null;
  ls_m: number | null;
}

export interface Municipio {
  id: number;
  nome: string;
  sindicato_id: number | null;
  sindicato_sigla?: string | null;
  taxa_iss: number | null;
  confirmado: boolean;
  observacoes: string | null;
}

export interface MunicipioResumido {
  id: number;
  nome: string;
}

export interface Orcamento {
  id: number;
  nome: string;
  municipio_id: number | null;
  familia_id: number | null;
  regime: Regime;
  criado_em: string;
  criado_por: number | null;
  observacoes: string | null;
  municipio_nome?: string | null;
}

export interface ItemBusca {
  id: number;
  codigo: string;
  descricao: string;
  classe: string | null;
  unidade: string | null;
  palavras_chave?: string | null;
  preco_unitario: number;
  regime?: Regime;
  sindicato?: string;
  ls_h?: number | null;
  ls_m?: number | null;
  diametro_mm?: number | null;
  material?: string | null;
  /** Só em /api/itens: total da busca (todas as páginas). */
  total_resultados?: number;
}

export interface OrcamentoItemCalculado {
  id: number;
  orcamento_id: number;
  item_id: number;
  familia_id: number | null;
  codigo_snapshot: string;
  descricao_snapshot: string;
  unidade_snapshot: string | null;
  quantidade: number;
  preco_unitario_snapshot: number;
  ordem: number | null;
  /** Seção/subseção a que o item pertence (null = sem seção). */
  secao_id: number | null;
  classe: string | null;
  /** Material (R$) contido no preço unitário, pela composição EMBASA; null = código sem decomposição. */
  custo_material: number | null;
  decomposicao_status: StatusDecomposicao;
  total: number;
  /** Composição EMBASA só com insumos de material: o preço inteiro é material. */
  so_material: boolean;
  /**
   * Peça acrescentada automaticamente: o código é só a instalação (composição
   * só de mão de obra) e o material vem da tabela de insumos EMBASA.
   */
  fornecimento: FornecimentoPeca | null;
  /** Preço unitário da tabela + fornecimento da peça (se houver). */
  preco_unitario: number;
  /** Material contido no próprio preço da tabela (sem o fornecimento acrescentado). */
  unit_material_tabela: number;
  /** Preço unitário dividido em material + serviço (material inclui o fornecimento). */
  unit_material: number;
  unit_servico: number;
  total_material: number;
  total_servico: number;
  natureza: NaturezaItem;
  tipo_bdi: TipoBdi;
}

export type NaturezaItem = "material" | "servico" | "misto";

export interface FornecimentoPeca {
  insumo_codigo: string;
  descricao: string;
  unidade: string | null;
  /** Preço do insumo × coeficiente, por unidade do código. */
  preco_unitario: number;
}

export interface ComposicaoCalculada {
  id: number;
  orcamento_id: number;
  tipo: TipoComposicao;
  descricao: string;
  codigo_referencia: string | null;
  unidade: string | null;
  quantidade: number;
  valor_unitario: number;
  ordem: number | null;
  /** 'canteiro' = gerada pela memória de cálculo do canteiro. */
  origem: "manual" | "canteiro";
  total: number;
}

export interface ItemCotacao {
  id: number;
  orcamento_id: number;
  /** Código que veio na planilha importada (não encontrado nas tabelas EMBASA). */
  codigo_planilha: string | null;
  descricao: string;
  unidade: string | null;
  quantidade: number;
  ordem: number | null;
}

/** Uma linha da planilha de quantitativos importada, já casada (ou não) com a tabela EMBASA. */
export type StatusImportacao = "codigo" | "descricao" | "nao_encontrado";

export interface ItemCasado {
  id: number;
  codigo: string;
  descricao: string;
  unidade: string | null;
  preco_unitario: number;
}

export interface LinhaImportada {
  /** Número da linha na planilha (para o usuário conferir). */
  linha: number;
  codigo_planilha: string | null;
  descricao_planilha: string;
  unidade_planilha: string | null;
  quantidade: number;
  /** Preço unitário que veio na planilha (orçamento antigo); null se não houver coluna de preço. */
  preco_planilha: number | null;
  status: StatusImportacao;
  /** Item da tabela EMBASA casado (por código ou descrição); null se não encontrado. */
  item: ItemCasado | null;
}

export interface PreviewImportacao {
  arquivo: string;
  total_linhas: number;
  linhas: LinhaImportada[];
}

/** Seção ou subseção do orçamento, já com o número calculado ("1", "1.2"...). */
export interface SecaoOrcamento {
  id: number;
  orcamento_id: number;
  pai_id: number | null;
  titulo: string;
  ordem: number;
}

export interface SecaoCalculada extends SecaoOrcamento {
  /** "1", "2" para seções; "1.1", "1.2" para subseções. */
  numero: string;
  nivel: 1 | 2;
  /** Total dos itens desta seção (não inclui as subseções). */
  total_itens: number;
}

export interface ResumoOrcamento {
  itens: OrcamentoItemCalculado[];
  /** Seções/subseções na ordem de exibição (seção seguida das suas subseções). */
  secoes: SecaoCalculada[];
  /** Linhas cuja parcela de material não pôde ser calculada por completo. */
  itens_sem_composicao: number;
  /** Itens importados sem preço na tabela — para cotação externa. */
  itens_cotacao: ItemCotacao[];
  blocos_especiais: Record<TipoComposicao, ComposicaoCalculada[]>;
  totais_especiais: Record<TipoComposicao, number>;
  total_especiais: number;
  subtotal_servicos: number;
  subtotal_servicos_com_especiais: number;
  subtotal_materiais: number;
  valor_base_bdi: number;
  municipio: Municipio | null;
  familia_nome: string | null;
  iss_pct: number | null;
  bdi_servicos_pct: number | null;
  bdi_servicos_versao: string | null;
  bdi_servicos_fonte: string | null;
  bdi_materiais_pct: number | null;
  bdi_materiais_versao: string | null;
  bdi_materiais_fonte: string | null;
  valor_bdi_servicos: number;
  valor_bdi_materiais: number;
  total_geral: number;
}

export interface OrcamentoDetalhe {
  orcamento: Orcamento;
  resumo: ResumoOrcamento;
  /** Parâmetros da memória de cálculo do canteiro (null = ainda não preenchida). */
  canteiro: import("./canteiro").ParametrosCanteiro | null;
  pode_gerenciar: boolean;
}

export interface Painel {
  stats: {
    itens: number;
    variantes_importadas: number;
    variantes_total: number;
    municipios: number;
    orcamentos: number;
  };
  familias: { nome: string; codigo: string; importadas: number; total: number }[];
  orcamentos: Orcamento[];
}

export interface CategoriaServico {
  chave: string;
  nome: string;
}

export interface ModeloResumo {
  id: number;
  codigo: string;
  nome: string;
  descricao: string | null;
  incluidos: number;
  total: number;
  cotacao_externa: number;
}

export interface ModeloItem {
  id: number;
  modelo_id: number;
  descricao: string;
  fonte: string | null;
  busca_sugerida: string | null;
  ordem: number | null;
  requer_cotacao_externa: boolean;
  incluido: boolean | null;
}

export interface ModeloDetalhe {
  orcamento: Orcamento;
  modelo: { id: number; codigo: string; nome: string; descricao: string | null };
  itens: ModeloItem[];
  pode_gerenciar: boolean;
}

export interface RespostaToken {
  access_token: string;
  token_type: "Bearer";
  expires_in: number;
}

export interface ErroApi {
  erro: string;
}

/** Nota dos documentos gerados sobre a classificação serviço x material. */
export const NOTA_MATERIAL_SERVICO =
  'Todos os itens contam como serviço, exceto os da seção cujo nome começa com "Materia" (Materiais, Material...) e das subseções dela, que contam como ' +
  "material; sobre cada grupo incide o BDI correspondente.";
